import React, { FC } from "react";
import styled from "styled-components";
import { PaymentMethodDetails } from "../models/MerchantApplicationInputModel";

const FieldBox = styled.div<{ $hasError?: boolean; $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0px 14px;
  border: 2px solid ${(props) => (props.$hasError ? "#ff4b55" : "#c3cad8")};
  border-radius: 10px;
  background-color: ${(props) => {
    if (props.$disabled) return "#eef0f4";
    return props.$hasError ? "#fdecec" : "#fff";
  }};

  &:focus-within {
    border-color: ${(props) => (props.$hasError ? "#ff4b55" : "#2563eb")};
  }

  input {
    width: 46px;
    height: 100%;
    border: none;
    outline: none;
    padding: 0px;
    font-size: 16px;
    line-height: 20px;
    color: #1f2937;
    background-color: transparent;

    /* The stepper below replaces the browser's own spin buttons. */
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0px;
    }
  }

  .unit {
    font-size: 16px;
    line-height: 20px;
    color: #64748b;
  }
`;

const Stepper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;

  span {
    display: block;
    width: 0px;
    height: 0px;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    cursor: pointer;

    &.up {
      border-bottom: 6px solid #64748b;
    }
    &.down {
      border-top: 6px solid #64748b;
    }
    &.disabled {
      cursor: not-allowed;
      opacity: 0.35;
    }
  }
`;

type IProps = {
  value: string;
  onChange: (value: string) => void;
  minPercentage?: number;
  maxPercentage?: number;
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
};

/**
 * Percentage field used by every surcharge control: the typed value, a stepper
 * clamped to the allowed range and the "%" unit, all inside one box.
 */
const SurchargePercentField: FC<IProps> = ({
  value,
  onChange,
  minPercentage = PaymentMethodDetails.minSurchargePercentage,
  maxPercentage = PaymentMethodDetails.maxSurchargePercentage,
  hasError,
  disabled,
  className,
}) => {
  const currentValue = parseFloat(value) || 0;
  const canStepUp = !disabled && currentValue < maxPercentage;
  const canStepDown = !disabled && currentValue > minPercentage;

  const onStep = (step: number) =>
    onChange(
      Math.min(
        maxPercentage,
        Math.max(minPercentage, currentValue + step),
      ).toFixed(2),
    );

  return (
    <FieldBox $hasError={hasError} $disabled={disabled} className={className}>
      <input
        type="number"
        step={minPercentage}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        /* An emptied field stays empty: coercing it to "0.00" here made the
           value impossible to clear and retype. Both models leave the rate
           empty by design and their validate() reports the range error for it,
           so an empty box blocks the save rather than saving a silent zero. */
        onBlur={(e) => {
          const parsed = parseFloat(e.target.value);
          onChange(isNaN(parsed) ? "" : parsed.toFixed(2));
        }}
      />
      <Stepper>
        <span
          className={`up ${canStepUp ? "" : "disabled"}`}
          onClick={() => canStepUp && onStep(minPercentage)}
        />
        <span
          className={`down ${canStepDown ? "" : "disabled"}`}
          onClick={() => canStepDown && onStep(-minPercentage)}
        />
      </Stepper>
      <span className="unit">%</span>
    </FieldBox>
  );
};

export default SurchargePercentField;
